import { useCallback, useEffect, useMemo, useState } from "react";
import {
  HiOutlineExclamationTriangle,
  HiOutlineXMark,
  HiOutlineEllipsisVertical,
  HiOutlineLockClosed,
  HiOutlineUsers,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineCreditCard,
  HiOutlineMagnifyingGlass,
  HiOutlineTrash,
  HiOutlineMagnifyingGlassCircle,
} from "react-icons/hi2";
import {
  Dropdown,
  Modal,
  Checkbox,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { usersApi, ISeatRemovalPreview } from "../../api";
import {
  IResponseError,
  ITeamMemberRecord,
  ITeamMembersResponse,
  UserStatus,
  IUser,
  ModalType,
} from "../../data-access";
import { useUser, useMobileDevice } from "../../hooks";
import { storeAuthActions } from "../../store/slices/auth";
import { storeTeamActions } from "../../store/slices/team";
import { storeModalsActions } from "../../store/slices/modals";
import { Avatar, FormButton, FormSelect, Panel } from "../../ui";
import "../../ui/simple-table";
import { DataTablePagination } from "../../ui/data-table/data-table-pagination/data-table-pagination";
import { paginate } from "../../ui/data-table/data-table-utils";
import {
  capitalize,
  notify,
  handleFailedRequest,
  updateExtensionCookies,
} from "../../util";
import "./team.scss";
import { syncAuthWithExtension } from "../../util/auth-sync";

export const Team = (): JSX.Element => {
  const switchableRoles = ["admin", "member"];

  const { currentUser, hasAdminRights, auth } = useUser();
  const isMobileResolution = useMobileDevice();
  const [open, setOpen] = useState(false);
  const [isHard, setIsHard] = useState(false);

  const [isInviteInProgress, setIsInviteInProgress] = useState<boolean>(false);
  const [isProcessingTeamMembers, setIsProcessingTeamMembers] =
    useState<boolean>(false);
  const [teamMembers, setTeamMembers] = useState<ITeamMemberRecord[]>(
    useSelector(
      (state: { team: { members: ITeamMemberRecord[] } }) => state.team.members
    )
  );
  const [currentDeleteUser, setCurrentDeleteUser] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set([0]));

  const toggleRow = (index: number): void => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<"all" | UserStatus>("all");
  const [seatRemovalPreview, setSeatRemovalPreview] = useState<ISeatRemovalPreview | null>(null);
  const [isLoadingRemovalPreview, setIsLoadingRemovalPreview] = useState(false);

  const dispatch = useDispatch();

  const handleCancel = () => {
    setOpen(false);
  };

  // first - owner, second - current user, then all the rest
  const reorderTeamMembers = useCallback(
    (membersList: ITeamMemberRecord[]): ITeamMemberRecord[] => {
      const reorderedList = [...membersList];

      if (membersList.length > 1) {
        const currentUserIndex = reorderedList.findIndex(
          (member: ITeamMemberRecord) => member.id === currentUser?.id
        );
        const currentUserRecordInArray = reorderedList.splice(
          currentUserIndex,
          1
        );
        reorderedList.unshift(currentUserRecordInArray[0]);

        const ownerIndex = reorderedList.findIndex(
          (member: ITeamMemberRecord) => member.role === "owner"
        );
        const ownerRecordInArray = reorderedList.splice(ownerIndex, 1);
        reorderedList.unshift(ownerRecordInArray[0]);
      }

      return reorderedList;
    },
    [currentUser?.id]
  );

  const stats = useMemo(
    () => ({
      total: teamMembers.length,
      active: teamMembers.filter((m) => m.is_verified).length,
      pending: teamMembers.filter((m) => !m.is_verified).length,
    }),
    [teamMembers]
  );

  const aiCreditsStats = useMemo(() => {
    const given = currentUser?.ai_credits_total || 0;
    const used = currentUser?.ai_credits_used || 0;
    return {
      remaining: given - used,
      total: given,
    };
  }, [currentUser?.ai_credits_total, currentUser?.ai_credits_used]);

  const filteredMembers = useMemo(() => {
    return teamMembers.filter((m) => {
      const matchesSearch =
        !searchQuery ||
        m.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole = roleFilter === "all" || m.role === roleFilter;
      return matchesSearch && matchesRole;
    });
  }, [teamMembers, searchQuery, roleFilter]);

  const roleTabs: { key: "all" | UserStatus; label: string }[] = [
    { key: "all", label: "All" },
    { key: "owner", label: "Owners" },
    { key: "admin", label: "Admins" },
    { key: "member", label: "Members" },
  ];

  // Need to fetch actual current user data to update cookie after team changes
  const requestCurrentUser = (): void => {
    usersApi.getMe().then((response: { data: IUser }) => {
      const userData = { user: response.data };
      dispatch(storeAuthActions.setUserData(userData));

      syncAuthWithExtension("AUTH_UPDATE", {
        user: response.data,
        accessToken: auth?.access_token,
        refreshToken: auth?.refresh_token,
      });
      updateExtensionCookies(userData);
    });
  };

  const requestTeamMembers = (): void => {
    setIsProcessingTeamMembers(true);
    usersApi
      .getTeamMembers()
      .then((response: ITeamMembersResponse) => {
        const properlyOrderedMembersList = reorderTeamMembers(response.data);
        setTeamMembers(properlyOrderedMembersList);
        dispatch(storeTeamActions.setMembers(properlyOrderedMembersList));
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text);
      })
      .finally(() => setIsProcessingTeamMembers(false));
  };

  const showDeleteUserConfirmation = (id: string): void => {
    setOpen(true);
    setIsHard(false);
    setCurrentDeleteUser(id);
    setSeatRemovalPreview(null);

    const target = teamMembers.find((m) => m.id === id);
    if (target?.paid_user && currentUser?.has_active_subscription) {
      setIsLoadingRemovalPreview(true);
      usersApi.previewSeatRemoval(id)
        .then(setSeatRemovalPreview)
        .catch(() => setSeatRemovalPreview(null))
        .finally(() => setIsLoadingRemovalPreview(false));
    }
  };

  const updateTeamMember = (updatedMember: ITeamMemberRecord): void => {
    const targetUserIndex = teamMembers.findIndex(
      (member: ITeamMemberRecord) => member.id === updatedMember.id
    );
    const members = [...teamMembers];
    members[targetUserIndex] = updatedMember;
    dispatch(storeTeamActions.setMembers(members));
    setTeamMembers(members);
  };

  const resendInvitation = (email: string): void => {
    setIsInviteInProgress(true);
    usersApi
      .resendInvitation(email)
      .then(() => {
        notify("success", "Invitation has been successfully sent");
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text);
      })
      .finally(() => setIsInviteInProgress(false));
  };

  const handleDeleteUserClick = (): void => {
    setIsProcessingTeamMembers(true);
    usersApi
      .deleteUser(currentDeleteUser, isHard)
      .then(() => {
        const modifiedTeamMembers = teamMembers.filter(
          (member: ITeamMemberRecord) => member.id !== currentDeleteUser
        );
        dispatch(storeTeamActions.setMembers(modifiedTeamMembers));
        setTeamMembers(modifiedTeamMembers);
        notify("success", "Member has been removed");
        setOpen(false);
        requestCurrentUser();
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text);
      })
      .finally(() => setIsProcessingTeamMembers(false));
  };

  const handleChangeRole = (id: string, role: UserStatus): void => {
    setIsProcessingTeamMembers(true);
    usersApi
      .changeRole(id, role)
      .then((response) => {
        updateTeamMember(response.data);
        notify("success", `Role has been successfully changed to ${role}`);
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text);
      })
      .finally(() => setIsProcessingTeamMembers(false));
  };

  const getTeamTableActions = (user: ITeamMemberRecord) => {
    return [
      // Resend invite (pending members only)
      ...(!user.is_verified && user.role !== "owner"
        ? [
            {
              key: "resendInvitation",
              label: "Resend Invitation",
              onClick: () => resendInvitation(user.email),
            },
          ]
        : []),

      // Change role
      ...(user.id !== currentUser?.id && switchableRoles.includes(user.role)
        ? [
            {
              key: "changeRole",
              label: `Make ${user.role === "admin" ? "Member" : "Admin"}`,
              onClick: () =>
                handleChangeRole(
                  user.id,
                  user.role === "admin" ? "member" : "admin"
                ),
            },
          ]
        : []),

      // Remove member
      ...(user.id !== currentUser?.id && user.role !== "owner"
        ? [
            {
              key: "delete",
              label: "Remove",
              onClick: () => showDeleteUserConfirmation(user.id),
            },
          ]
        : []),
    ];
  };

  const getActionsDisplay = (
    teamMember: ITeamMemberRecord
  ): JSX.Element | null => {
    if (hasAdminRights) {
      const availableActions = getTeamTableActions(teamMember);
      return availableActions.length > 0 ? (
        <Dropdown menu={{ items: availableActions }}>
          <span className="action-icon">
            <HiOutlineEllipsisVertical size={20} />
          </span>
        </Dropdown>
      ) : (
        <span className="action-icon action-icon--disabled">
          <HiOutlineLockClosed size={16} />
        </span>
      );
    }

    return null;
  };

  const isTrial = currentUser?.billing_status === 'trial';
  const isPastDue = currentUser?.billing_status === 'past_due';
  const isCanceled = currentUser?.billing_status === 'canceled';

  const getStatusBadge = (member: ITeamMemberRecord): JSX.Element => {
    if (!member.is_verified) return <span className="seat-badge seat-badge--pending">Pending</span>;
    if (isCanceled) return <span className="seat-badge seat-badge--failed">Canceled</span>;
    if (isPastDue) return <span className="seat-badge seat-badge--failed">Past Due</span>;
    if (isTrial) return <span className="seat-badge seat-badge--trial">Trial</span>;
    if (member.paid_user) return <span className="seat-badge seat-badge--active">Active</span>;
    return <span className="seat-badge seat-badge--none">No Plan</span>;
  };

  const getDisplayName = (member: ITeamMemberRecord): string =>
    member.full_name ?? member.email.substring(0, member.email.indexOf("@"));

  const getInitials = (name: string): string =>
    name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);

  const handlePagination = (perPage: number, requestedPage: number): void => {
    const paginateData = paginate(filteredMembers, perPage, requestedPage);
    const actualPage =
      paginateData.length > 0
        ? requestedPage
        : Math.ceil(filteredMembers.length / perPage);
    setPerPage(perPage);
    setCurrentPage(actualPage);
    setExpandedRows(new Set([0]));
  };

  const showInvitationModal = (): void => {
    dispatch(
      storeModalsActions.open({
        type: ModalType.InviteTeamMembers,
        props: {
          onSuccess: () => {
            requestTeamMembers();
            requestCurrentUser();
          },
        },
      })
    );
  };

  useEffect(() => {
    requestCurrentUser();
    requestTeamMembers();
  }, []);

  const inviteButtonComponent = hasAdminRights ? (
    <FormButton className="invite-button" theme="green-light" onClick={showInvitationModal}>
      Invite +
    </FormButton>
  ) : null;

  return (
    <Panel
      title="Team"
      className="team-panel"
      nodeInTitleRow={inviteButtonComponent}
    >
      <div className="team">
        {isProcessingTeamMembers ? (
          <div className="team__hero">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="team__stat">
                <div className="team__shimmer team__shimmer--value" />
                <div className="team__shimmer team__shimmer--label" />
              </div>
            ))}
          </div>
        ) : (
          <div className="team__hero">
            <div className="team__stat">
              <span className="team__value">{stats.total}</span>
              <span className="team__label">
                <HiOutlineUsers size={16} />
                Total Members
              </span>
            </div>
            <div className="team__stat">
              <span className="team__value">{stats.active}</span>
              <span className="team__label">
                <HiOutlineCheckCircle size={16} />
                Active
              </span>
            </div>
            <div className="team__stat">
              <span className="team__value team__value--warning">
                {stats.pending}
              </span>
              <span className="team__label">
                <HiOutlineClock size={16} />
                Pending
              </span>
            </div>
            <div className="team__stat">
              <span className="team__value">{aiCreditsStats.remaining}</span>
              <span className="team__label">
                <HiOutlineCreditCard size={16} />
                AI Credits
                <span className="team__label-detail">of {aiCreditsStats.total}</span>
              </span>
            </div>
          </div>
        )}

        <div className="team__divider" />

        <div className="team__toolbar">
          <div className="team__search">
            <HiOutlineMagnifyingGlass size={16} className="team__search-icon" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <ul className="team__role-tabs">
            {roleTabs.map((tab) => (
              <li
                key={tab.key}
                className={roleFilter === tab.key ? "active" : ""}
                onClick={() => { setRoleFilter(tab.key); setCurrentPage(1); }}
              >
                {tab.label}
              </li>
            ))}
          </ul>
          <div className="team__role-select">
            <FormSelect
              options={roleTabs.map((t) => ({ value: t.key, label: t.label }))}
              value={roleFilter}
              onChange={(val) => { setRoleFilter(val as "all" | UserStatus); setCurrentPage(1); }}
            />
          </div>
        </div>

        {!isProcessingTeamMembers && filteredMembers.length === 0 && (searchQuery || roleFilter !== "all") ? (
          <div className="team__empty-search">
            <HiOutlineMagnifyingGlassCircle size={40} />
            <h4>No members found</h4>
            <p>Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          <div className="simple-table team__table">
            <div className="simple-table__header team__grid">
              <span className="simple-table__col simple-table__col--member">Member</span>
              <span className="simple-table__col">Status</span>
              <span className="simple-table__col">Role</span>
              <span className="simple-table__col simple-table__col--right">Action</span>
            </div>
            {(isProcessingTeamMembers || isInviteInProgress) ? (
              [0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="simple-table__row simple-table__row--expanded team__grid">
                  <div className="simple-table__col simple-table__col--member" data-label="">
                    <div className="team__tbl-shimmer team__tbl-shimmer--avatar" />
                    <div>
                      <div className="team__tbl-shimmer team__tbl-shimmer--name" />
                      <div className="team__tbl-shimmer team__tbl-shimmer--email" />
                    </div>
                  </div>
                  <span className="simple-table__col" data-label="Status"><div className="team__tbl-shimmer team__tbl-shimmer--badge" /></span>
                  <span className="simple-table__col" data-label="Role"><div className="team__tbl-shimmer team__tbl-shimmer--badge" /></span>
                  <span className="simple-table__col simple-table__col--right" data-label="Actions"><div className="team__tbl-shimmer team__tbl-shimmer--action" /></span>
                </div>
              ))
            ) : (
              paginate(filteredMembers, perPage, currentPage).map((teamMember, index) => {
                const displayName = getDisplayName(teamMember);
                const initials = getInitials(displayName);

                return (
                  <div
                    key={teamMember.id}
                    className={`simple-table__row team__grid${expandedRows.has(index) ? " simple-table__row--expanded" : ""}`}
                    onClick={(e) => {
                      const header = e.currentTarget.firstElementChild;
                      if (header && header.contains(e.target as Node)) toggleRow(index);
                    }}
                  >
                    <div className="simple-table__col simple-table__col--member" data-label="">
                      <div className="member-cell">
                        {teamMember.picture ? (
                          <Avatar imageSrc={teamMember.picture} />
                        ) : (
                          <span className="member-cell__initials">{initials}</span>
                        )}
                        <div className="member-cell__info">
                          <span className="member-cell__name">
                            {displayName}
                            {!teamMember.is_verified && (
                              <span className="member-cell__pending">Pending</span>
                            )}
                          </span>
                          <span className="member-cell__email">{teamMember.email}</span>
                        </div>
                      </div>
                    </div>
                    <span className="simple-table__col" data-label="Status">
                      {getStatusBadge(teamMember)}
                    </span>
                    <span className="simple-table__col" data-label="Role">
                      <span className={`role-badge role-badge--${teamMember.role}`}>
                        <span className="role-badge__dot" />
                        {capitalize(teamMember.role)}
                      </span>
                    </span>
                    <span className="simple-table__col simple-table__col--right" data-label="Actions">
                      {getActionsDisplay(teamMember)}
                    </span>
                  </div>
                );
              })
            )}
            <DataTablePagination
              perPage={perPage}
              perPageOptions={[10, 25, 50]}
              total={filteredMembers.length}
              currentPage={currentPage}
              onPagination={handlePagination}
            />
          </div>
        )}
      </div>

      <Modal
        open={open}
        className="delete-user-modal"
        closeIcon={<HiOutlineXMark size={20} />}
        onCancel={handleCancel}
        footer={null}
        title={null}
        width={380}
      >
        {(() => {
          const target = teamMembers.find((m) => m.id === currentDeleteUser);
          const showSeatReduction = seatRemovalPreview?.will_reduce_seat;
          const intervalLabel = seatRemovalPreview?.interval === 'yearly' ? 'yr' : 'mo';
          const formatCurrency = (amount: number, currency: string = 'usd') => {
            const symbol = currency === 'usd' ? '$' : currency.toUpperCase() + ' ';
            return `${symbol}${amount.toFixed(2)}`;
          };

          return (
            <div className="delete-user-content">
              <div className="delete-user-content__icon">
                <HiOutlineTrash size={24} />
              </div>
              <h3 className="delete-user-content__title">Remove Member</h3>
              {target ? (
                <p className="delete-user-content__desc">
                  <strong>{target.full_name ?? target.email}</strong> will be
                  permanently removed from your team.
                </p>
              ) : (
                <p className="delete-user-content__desc">
                  This action is permanent and cannot be undone.
                </p>
              )}

              {isLoadingRemovalPreview && (
                <div className="delete-user-content__billing-note">
                  <div className="seat-cost">
                    <div className="seat-cost__shimmer" />
                    <div className="seat-cost__shimmer seat-cost__shimmer--short" />
                  </div>
                </div>
              )}

              {showSeatReduction && seatRemovalPreview && (
                <div className="delete-user-content__billing-note">
                  <HiOutlineCreditCard size={16} />
                  <div className="seat-cost">
                    <span className="seat-cost__title">Subscription will be reduced</span>
                    <div className="seat-cost__row">
                      <span className="seat-cost__label">Seats</span>
                      <span className="seat-cost__value">{seatRemovalPreview.current_seats} &rarr; {seatRemovalPreview.new_seats}</span>
                    </div>
                    <div className="seat-cost__row">
                      <span className="seat-cost__label">AI credits</span>
                      <span className="seat-cost__value">Kept until renewal</span>
                    </div>
                    <div className="seat-cost__divider" />
                    <div className="seat-cost__row">
                      <span className="seat-cost__label">New {seatRemovalPreview.interval === 'yearly' ? 'yearly' : 'monthly'} total</span>
                      <span className="seat-cost__value">{formatCurrency(seatRemovalPreview.new_total!, seatRemovalPreview.currency!)}/{intervalLabel}</span>
                    </div>
                  </div>
                </div>
              )}

              <Checkbox
                checked={isHard}
                onChange={(value) => setIsHard(value.target.checked)}
              >
                Include analytics data
              </Checkbox>
              <div className="delete-user-content__buttons">
                <FormButton theme="grey" onClick={handleCancel} disabled={isProcessingTeamMembers}>
                  Cancel
                </FormButton>
                <FormButton
                  theme="red"
                  className="delete-button"
                  loading={isProcessingTeamMembers}
                  disabled={isProcessingTeamMembers || isLoadingRemovalPreview}
                  onClick={handleDeleteUserClick}
                >
                  {isProcessingTeamMembers ? 'Removing...' : showSeatReduction ? 'Remove & Reduce Seat' : 'Remove'}
                </FormButton>
              </div>
            </div>
          );
        })()}
      </Modal>
    </Panel>
  );
};

export default Team;
